import { useEffect, useRef } from "react";
import { Check, Info, X } from "lucide-react";

export type ToastMessage = { text: string; error: boolean; tone?: "success" | "error" | "info" };

/**
 * The short confirmation at the bottom of the screen ("Invoice approved.").
 * Announced politely (errors assertively) and dismissible; the shell clears
 * it after a few seconds.
 */
export function Toast({ toast, onClose }: { toast: ToastMessage | null; onClose: () => void }) {
  if (!toast) return null;
  const tone = toast.tone ?? (toast.error ? "error" : "success");
  const Icon = tone === "error" ? X : tone === "info" ? Info : Check;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? "assertive" : "polite"}
      className={`toast ${tone === "error" ? "toast-error" : ""} toast-${tone}`}
    >
      <Icon size={18} aria-hidden="true" /> <span className="toast-text">{toast.text}</span>
      <button type="button" className="toast-close" aria-label="Dismiss message" onClick={onClose}>
        <X size={14} />
      </button>
    </div>
  );
}

const TOAST_EVENT = "parlyst:toast";

/** Shows a toast from anywhere (a drawer, a hook) without passing `notify` down. */
export function showToast(text: string, error = false) {
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: { text, error } }));
}

/** The app shell routes showToast() calls into its own notify(). */
export function useToastEvents(notify: (text: string, error?: boolean) => void) {
  const ref = useRef(notify);
  ref.current = notify;
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ text: string; error: boolean }>).detail;
      if (d?.text) ref.current(d.text, !!d.error);
    };
    window.addEventListener(TOAST_EVENT, on);
    return () => window.removeEventListener(TOAST_EVENT, on);
  }, []);
}
